/** Incidencias: cola (mediación primero) con resolución desde admin (marketplace-resolveDispute). */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import type { DisputeAssistantOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, when, money, str } from '../lib/ui.js';

type Doc = Record<string, unknown>;
const OPEN = ['in_mediation', 'open', 'awaiting_seller', 'awaiting_buyer', 'awaiting_return'];

export function DisputesPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [partial, setPartial] = useState<Record<string, string>>({});
  const [version, setVersion] = useState(0);
  const [totals, setTotals] = useState<Record<string, Doc>>({});
  const [ai, setAi] = useState<Record<string, DisputeAssistantOutput | 'loading'>>({});
  const askAi = (id: string): void => {
    setAi((a) => ({ ...a, [id]: 'loading' }));
    call(api.ai.disputeAssistant, { dispute_id: id })
      .then((out) => {
        setAi((a) => ({ ...a, [id]: out }));
      })
      .catch((e: unknown) => {
        setAi((a) => {
          const { [id]: _drop, ...rest } = a;
          return rest;
        });
        setError(errorText(e));
      });
  };
  useEffect(() => {
    getDocs(
      query(
        collection(db, 'disputes'),
        where('status', 'in', OPEN),
        orderBy('resolve_by', 'asc'),
        limit(100),
      ),
    )
      .then(async (s) => {
        const list: Doc[] = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        const map: Record<string, Doc> = {};
        await Promise.all(
          list.map(async (d) => {
            const o = await getDoc(doc(db, 'orders', String(d.order_id))).catch(() => null);
            const total = (o?.get('amounts') as Doc | undefined)?.total as Doc | undefined;
            if (total) map[String(d.id)] = total;
          }),
        );
        setTotals(map);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [version]);
  const resolve = (d: Doc, kind: 'refund_full' | 'refund_partial' | 'release'): void => {
    const id = String(d.id);
    const total = totals[id] ?? null;
    let refund: { amount: number; currency: string } | undefined;
    if (kind === 'refund_partial') {
      const cur = (total?.currency as string | undefined) ?? 'EUR';
      const n = Number((partial[id] ?? '').replace(',', '.'));
      if (!Number.isFinite(n) || n <= 0) {
        setError('Importe parcial no válido.');
        return;
      }
      const digits = ['HUF', 'ISK'].includes(cur) ? 0 : 2;
      refund = { amount: Math.round(n * 10 ** digits), currency: cur };
    }
    if (!window.confirm(`¿Resolver la incidencia como ${kind}?`)) return;
    setError(null);
    call(api.marketplace.resolveDispute, {
      dispute_id: id,
      kind,
      ...(refund ? { refund_amount: refund as never } : {}),
      ...(note[id] ? { note: note[id] } : {}),
    })
      .then(() => {
        setNotice('Incidencia resuelta.');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Incidencias abiertas</h1>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows?.length === 0 && <p style={muted}>No hay incidencias abiertas.</p>}
      {rows?.map((d) => {
        const id = String(d.id);
        return (
          <section key={id} style={card}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <strong>
                {String(d.status)} · {String(d.reason)} ·{' '}
                <Link to={`/pedidos/${String(d.order_id)}`}>
                  pedido {String(d.order_id).slice(-6).toUpperCase()}
                </Link>
              </strong>
              <span style={muted}>
                abierta {when(d.created_at)} · resolver antes de {when(d.resolve_by)}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>{str(d.description)}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {((d.photo_urls as string[] | undefined) ?? []).map((u) => (
                <a key={u} href={u} target="_blank" rel="noreferrer">
                  <img
                    src={u}
                    alt=""
                    width={72}
                    height={72}
                    style={{ objectFit: 'cover', borderRadius: 8 }}
                  />
                </a>
              ))}
            </div>
            {d.seller_response ? (
              <p style={{ margin: 0, fontSize: 13 }}>
                Vendedor: {JSON.stringify(d.seller_response)}
              </p>
            ) : null}
            {d.return ? (
              <p style={{ margin: 0, fontSize: 13 }}>Devolución: {JSON.stringify(d.return)}</p>
            ) : null}
            <AiBlock
              state={ai[id]}
              onAsk={() => {
                askAi(id);
              }}
            />
            <input
              style={field}
              placeholder="Nota de resolución (la ven las dos partes)"
              value={note[id] ?? ''}
              onChange={(e) => {
                setNote((n) => ({ ...n, [id]: e.target.value }));
              }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                style={btn(true)}
                onClick={() => {
                  resolve(d, 'refund_full');
                }}
              >
                Reembolso total al comprador
              </button>
              <input
                style={{ ...field, width: 120 }}
                placeholder="importe"
                value={partial[id] ?? ''}
                onChange={(e) => {
                  setPartial((p) => ({ ...p, [id]: e.target.value }));
                }}
              />
              <button
                type="button"
                style={btn(false)}
                onClick={() => {
                  resolve(d, 'refund_partial');
                }}
              >
                Reembolso parcial
              </button>
              <button
                type="button"
                style={btn(false)}
                onClick={() => {
                  resolve(d, 'release');
                }}
              >
                Liberar el pago al vendedor
              </button>
              <span style={muted}>{totals[id] ? `total ${money(totals[id])}` : ''}</span>
            </div>
          </section>
        );
      })}
    </>
  );
}

const REC: Record<DisputeAssistantOutput['recommendation'], string> = {
  refund_full: 'Reembolso total',
  refund_partial: 'Reembolso parcial',
  release: 'Liberar el pago',
  need_more_info: 'Falta información',
};

function AiBlock({
  state,
  onAsk,
}: {
  state: DisputeAssistantOutput | 'loading' | undefined;
  onAsk: () => void;
}): ReactElement {
  if (!state)
    return (
      <div>
        <button type="button" style={btn(false)} onClick={onAsk}>
          Asistente IA: resumir y proponer resolución
        </button>
      </div>
    );
  if (state === 'loading') return <p style={muted}>Analizando la incidencia…</p>;
  return (
    <div style={{ ...card, background: '#f6f8ff', fontSize: 13, gap: 4 }}>
      <strong>
        Propuesta IA: {REC[state.recommendation]}
        {state.recommended_refund_percent !== null
          ? ` (${state.recommended_refund_percent} %)`
          : ''}
        {state.cached ? ' · caché' : ''}
      </strong>
      <p style={{ margin: 0 }}>{state.summary}</p>
      <p style={{ margin: 0 }}>Comprador: {state.buyer_claim}</p>
      <p style={{ margin: 0 }}>Vendedor: {state.seller_position}</p>
      <p style={{ margin: 0 }}>Fotos: {state.photos_assessment}</p>
      <p style={{ margin: 0 }}>Motivo: {state.rationale}</p>
      <span style={muted}>Es una ayuda: la decisión la tomas tú con los botones de abajo.</span>
    </div>
  );
}
